import { Overlay } from '@angular/cdk/overlay';
import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SafeHtml } from '@angular/platform-browser';
import { CalioColumnConfigData } from '@calio-lib/calio-column-config/calio-column-config.model';
import { CalioDialogComponent } from '@calio-lib/calio-dialog/calio-dialog.component';
import type { SwStepFullscreenData } from '@dashboard/settings/pages/smart-widget-process/setup-smart-widget-dashboard/pages/smart-widget-unified-setup/sw-step-fullscreen-dialog.model';
import { BryntumCalendarEventModel, BryntumCalendarResourceModel } from '@client-models/bryntum-calendar-model';
import { WebhookEndpoints } from '@client-models/webhook-stats.model';
import { AbsenceConflictAction } from '@constants/absence.constants';
import { TestSendChannel } from '@constants/workflows.constants';
import { AbsenceConflictAppointment, AbsenceDbModel } from '@db-models/absence.model';
import { ActiveSubscriptionDbModel } from '@db-models/active-subscription.model';
import { AddOnModel, AddOnPartnerModel } from '@db-models/add-on-db.model';
import { AnalyticsFilterConfig, AnalyticsFilterPresetModel } from '@db-models/analytics.model';
import { ApiErrorDialog } from '@db-models/api-error.model';
import { AppointmentServiceCategoryDbModel } from '@db-models/appointment-service-category-db.model';
import { AppointmentServiceDbModel } from '@db-models/appointment-service.model';
import { AppointmentStatusDbModel, AppointmentStatusModel } from '@db-models/appointment-status-db';
import { AuditLogsDbModel } from '@db-models/audit-logs.model';
import { AvailabilityDbModel } from '@db-models/availability-db.model';
import { StoreWorkerAssignment } from '@db-models/stores-worker.model';
import { AzureStoreWorkerModel } from '@db-models/azure-store-worker.model';
import { AzureRoleMappingModel, AzureRoleSyncStartResponse } from '@db-models/azure-role-mapping.model';
import { BookingLimitRuleDbModel } from '@db-models/booking-limit-override.model';
import { BookingLinkGenerator } from '@db-models/booking-link-generator.model';
import { BookingTypeAttributeDbModel } from '@db-models/booking-type-attributes.model';
import { BookingTypesDbModel } from '@db-models/booking-types.model';
import { CalendarAppointmentModel } from '@db-models/calendar-appointment.model';
import { CategoryDbModel } from '@db-models/category-db.model';
import { ConferenceWorkerModel } from '@db-models/conference-db.model';
import { ConfirmationModal } from '@db-models/confirmation-dialog.model';
import { ForecastDayDetailData } from '@dashboard/settings/pages/analytics/forecast-tab/components/day-detail-dialog/day-detail-dialog.component';
import { CountryDbModel } from '@db-models/country.model';
import { CustomFieldModel, CustomFieldOptionModel } from '@db-models/custom-field.model';
import { CustomBookingDbModel, CustomerCustomFieldDbModel, CustomerDbModel, CustomerNotesDbModel, CustomerUpcomingAppointmentModel, EventParticipantDbModel } from '@db-models/customer-db.model';
import { CustomerTypeDbModel } from '@db-models/customer-type-db.model';
import { DealsDbModel } from '@db-models/deals.model';
import { BookingEventDao } from '@db-models/event-booking.dao';
import { EventDbModel, EventSlotDbModel } from '@db-models/event-db.model';
import { MeetingsDbModel } from '@db-models/meetings-db.model';
import { MfaDbModel } from '@db-models/mfa.models';
import { NotificationDbModel } from '@db-models/notification-db.model';
import { CloneType, PartnerDbModel } from '@db-models/partner-db.model';
import { PartnerSettingModel } from '@db-models/partner-payment-account.model';
import { PartnershipDbModel } from '@db-models/partnership-db.model';
import { PostCodeDataModel } from '@db-models/postcode-db.model';
import { PartnerReleaseNotesModel, ReleaseNotesModel } from '@db-models/release-notes.model';
import { SmartWidgetStepModel } from '@db-models/smart-widget.model';
import { SpamPreventionDBModel } from '@db-models/spam-prevention-db.model';
import { StoreDbModel } from '@db-models/store-db.model';
import { StripeCreditCardModel } from '@db-models/stripe-credit-card.model';
import { TaxDbModel } from '@db-models/tax.model';
import { TemplateDbModel } from '@db-models/template.model';
import { WidgetAppointmentGroupModel } from '@db-models/widget-appointment-group.model';
import { WidgetConfiguration } from '@db-models/widget-configurations.model';
import { WorkerDbModel } from '@db-models/worker-db.model';
import { WorkflowsModel } from '@db-models/workflow-db.model';
import { ImportDynamicComponentService } from '@util-services/import-dynamic-component.service';

@Injectable({
  providedIn: 'root'
})
export class DialogService {

  private matDialog = inject(MatDialog);
  private overlay = inject(Overlay);
  private importDynamicComponentService = inject(ImportDynamicComponentService);

  /** Fullscreen smart-widget step editor (customer / booking-flow) via the standard
   *  CalioDialogComponent wrapper. The `sw-step-fullscreen-dialog` panelClass sizes it 95vw×95vh. */
  openSwStepFullscreenDialog(compData: SwStepFullscreenData): MatDialogRef<CalioDialogComponent, unknown> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importSwStepFullscreenDialogComp(),
        compData,
        dialogTitle: compData.titleKey,
        disableDrag: true,
      },
      width: '95vw',
      height: '95vh',
      maxWidth: '95vw',
      panelClass: 'sw-step-fullscreen-dialog',
      autoFocus: false,
    });
  }

  openEditGuestDialog(guest: {
    uuid?: string;
    customer?: {
      uuid?: string;
      prename?: string;
      lastname?: string;
      email?: string;
      phone?: string;
    };
  }, options: {
    useLandingPageAuth?: boolean;
    isEmailRequired?: boolean;
    isPhoneRequired?: boolean;
  } = {}): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importEditGuestDialogComp(),
        compData: {
          guest,
          useLandingPageAuth: options.useLandingPageAuth === true,
          isEmailRequired: options.isEmailRequired === true,
          isPhoneRequired: options.isPhoneRequired === true,
        },
        dialogTitle: 'guest_detail_edit_dialog.title',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '500px',
    });
    return dialogRef;
  }

  openTemplateEventDialog(data: {
    template?: TemplateDbModel,
    activeSubscriptionId?: number,
    templateType?: string,
    appointmentSubjectTemplate?: TemplateDbModel,
    eventSubjectTemplate?: TemplateDbModel,
    appointmentMailTemplate?: TemplateDbModel,
    eventMailTemplate?: TemplateDbModel,
    showTemplateTypeSwitcher?: boolean,
    showTestEmailButton?: boolean
    disabled?: boolean
  }): MatDialogRef<any> {
    let title: string;
    if (data.template && data.template.type === 'email') {
      title = 'templates_component.set_template';
    } else if (data.template && data.template.type === 'sms') {
      title = 'templates_component.set_template';
    } else if (data.template && data.template.type === 'general') {
      title = 'templates_component.set_template';
    } else if (data.templateType === 'complete-email') {
      title = 'templates_component.set_template';
    }
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importTemplateFormDialogComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '550px',
    });
    return dialogRef;
  }

  openCalioSettingsDialog(partnerSettings: PartnerSettingModel[]): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importCalioSettingFormComp(),
        compData: {
          partnerSettings
        },
        dialogTitle: 'calio_settings_component.dashboard_settings',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '550px',
      maxWidth: '550px'
    });
    return dialogRef;
  }

  openCustomerNoteFormDialog(customerNote: CustomerNotesDbModel): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importCustomerNotesFormComp(),
        compData: {
          customerNote
        },
        dialogTitle: 'crm_component.customer_name_title',
      },
      panelClass: 'calenso-generic-dialog-box',
      maxWidth: '450px',
    });
    return dialogRef;
  }

  openCreateRoleDialog(partnerUuid: string | undefined, isSuperAdmin: boolean): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importCreateRoleDialogComp(),
        // partnerUuid is the default selection for super-admin's partner picker
        // (carried from the list-page filter, may be undefined); for partner-
        // admin it's their own partner_uuid (used implicitly, no picker shown).
        compData: { partnerUuid, isSuperAdmin },
        dialogTitle: 'roles_permissions_settings.create_role',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '440px',
      autoFocus: false,
    });
  }

  openCreateSettingDialog(compData: {
    [key: string]: string | number
  }): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importCreateSettingComponent(),
        compData,
        dialogTitle: 'crm_component.mps_create_title',
      },
      panelClass: 'calenso-generic-dialog-box',
      maxWidth: '450px',
    });
  }

  openConditionBuilderDialog(data: {
    conditions: any;
    availableFields: any[];
    triggerIdentifier?: string;
    /** True opens the dialog in view-only mode (Cancel button only, no inputs editable). */
    readonly?: boolean;
  }): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importConditionBuilderDialogComp(),
        compData: data,
        dialogTitle: 'workflow.conditions_title',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '800px',
      autoFocus: false,
    });
  }

  openQuestionFieldFormDialog(
    data: {
      customFieldDbModel: CustomFieldModel,
      formType: string
    }, title: string
  ): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importQuestionFieldFormComp(),
        compData: data,
        dialogTitle: title,
      },
      width: '600px',
      panelClass: 'calenso-generic-dialog-box',
    });
    return dialogRef;
  }

  openDealFormDialog(
    data: {
      deal: DealsDbModel,
      formType: string
    }, title: string
  ): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importDealFormComp(),
        compData: data,
        dialogTitle: title,
      },
      width: '500px',
      panelClass: 'calenso-generic-dialog-box',
    });
    return dialogRef;
  }

  openCompanySettingTranslationFormDialog(partnerDbModel: PartnerDbModel): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importCompanySettingTranslationFormComp(),
        compData: {
          partnerDbModel
        },
        dialogTitle: 'settings_component.company_description_title',
      },
      width: '500px',
      panelClass: 'calenso-generic-dialog-box',
    });
    return dialogRef;
  }

  openStripeCardDialog(stripeCardModel: StripeCreditCardModel, dialogTitle: string): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importStripeCardDialogComp(),
        compData: {
          stripeCardModel
        },
        dialogTitle,
      },
      width: '500px',
      panelClass: 'calenso-generic-dialog-box',
    });
    return dialogRef;
  }

  openResellerInviteDialog(partnershipDB: PartnershipDbModel): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importResellerInviteComp(),
        compData: {
          partnershipDB
        },
        dialogTitle: 'reseller_component.reseller_invite_title',
      },
      width: '500px',
      panelClass: 'calenso-generic-dialog-box',
    });
    return dialogRef;
  }

  openServiceCategoryDialog(data: {
    showCancelButton: boolean, formType: string, appointmentServiceCategoryDbModel: AppointmentServiceCategoryDbModel
  }, title: string): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importServiceCategoryComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '500px',
    });
    return dialogRef;
  }

  openAppointmentServiceDialog(data: {
    appointmentServiceDbModel: AppointmentServiceDbModel,
    formType: string,
    leadFollowUp: boolean,
  }, title: string): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importAppointmentServiceComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '700px',
    });
    return dialogRef;
  }

  openAppointmentQuestionServiceDialog(data: {
    appointmentServiceDbModel: AppointmentServiceDbModel
  }): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importAppointmentServiceQuestionComp(),
        compData: data,
        dialogTitle: 'appointment_services_questions_component.title',
        dialogSubTitle: 'event_question_fields_component.you_manage_appointment_service',
        dialogSubtitleTranslationJson: { appointment_service: data.appointmentServiceDbModel.name }
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '1000px',
    });
    return dialogRef;
  }

  openEmployeeFormDialog(data: {
    formType?: string,
    worker?: WorkerDbModel,
    workerDbModels?: WorkerDbModel[]
  }): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importEmployeeFormComp(),
        compData: data,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '600px',
    });
    return dialogRef;
  }

  openStoresFormDialog(
    data: {
      formType: any,
      storeDbModel: any,
    },
    title: string
  ): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importStoreFormComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '500px',
    });
    return dialogRef;
  }

  openWidgetAppointmentGroupFormDialog(data: {
    formType: string,
    widgetAppointmentGroupDbModel: WidgetAppointmentGroupModel,
  }, title: string): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importWidgetAppointmentGroupFormComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '600px',
    });
    return dialogRef;
  }

  openCalendarSetupDialog(
    data: {
      code?: string,
      calendarType?: string,
      workerId?: number,
      showWorkerOptionsInForm?: boolean,
      redirectTo?: boolean,
      workerCalenderList?: boolean[]
    },
    title: string): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importCalendarSetupDialogComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '520px',
    });
    return dialogRef;
  }

  openRecurringAvailabilitiesFormDialog(data: {
    worker: WorkerDbModel,
    screenType: string,
    availability?: AvailabilityDbModel | AvailabilityDbModel[],
    formType: string,
    showWorkerOptionsInForm: boolean,
    storeId?: number | null,
    storeName?: string,
    showStoreScope?: boolean,
    workerStores?: StoreWorkerAssignment[],
  }, title: string): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importAvailabilitiesFormSwitcherComp(),
        compData: data,
        dialogTitle: title,
      },
      width: '550px',
      panelClass: 'calenso-generic-dialog-box',
    });
    return dialogRef;
  }

  openAbsenceConflictDialog(
    conflictingAppointments: AbsenceConflictAppointment[]
  ): MatDialogRef<CalioDialogComponent, { action: AbsenceConflictAction } | undefined> {
    const dialogRef: MatDialogRef<CalioDialogComponent, { action: AbsenceConflictAction } | undefined> = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importAbsenceConflictDialogComp(),
        compData: { conflictingAppointments },
        dialogTitle: 'absence_conflict_dialog.title',
      },
      width: '640px',
      autoFocus: false,
      panelClass: 'calenso-generic-dialog-box',
    });
    return dialogRef;
  }

  openAbsencesFormDialog(
    data: {
      // Optional — the "Applies to" seg-control can create a Company-wide/Stores
      // absence with no pre-selected employee (absences-settings page header Add).
      worker?: WorkerDbModel | null,
      partnerDbModel: PartnerDbModel,
      showWorkerOptionsInForm: boolean,
      absense?: AbsenceDbModel,
      // Multi-store: the caller's active pill scope pre-selects the worker-absence
      // store field (null = Global); for a store-scope add it pre-selects the store.
      storeId?: number | null,
      // ADD: default the type dropdown to the caller's active sidebar scope
      // (ABSENCE_TYPE value) instead of always Employee.
      initialAbsenceType?: string | null,
      initialServiceId?: number | null
    },
    title: string
  ): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importAbsensesFormComp(),
        compData: data,
        dialogTitle: title,
      },
      width: '550px',
      panelClass: 'calenso-generic-dialog-box',
    });
    return dialogRef;
  }

  openCustomerTypesFormDialog(data: {
    formType: string,
    customerTypeDbModel: CustomerTypeDbModel,
  }, title: string): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importCustomerTypesFormComp(),
        compData: data,
        dialogTitle: title,
      },
      width: '500px',
      panelClass: 'calenso-generic-dialog-box',
    });
    return dialogRef;
  }

  openCalendarTypesFormDialog(data: {
    formType: string,
    categoryDbModel: CategoryDbModel,
  }, title: string): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importCreateCalendarTypeComp(),
        compData: data,
        dialogTitle: title,
      },
      width: '500px',
      panelClass: 'calenso-generic-dialog-box',
    });
    return dialogRef;
  }

  openPartnerSettingDialog(data: {
    setting_id?: number,
    template_type?: string,
    appointment_setting_id?: number,
    appointment_title?: string,
    event_setting_id?: number,
    placeholder?: string,
    event_title?: string,
    showAppointmentEventSwitch?: boolean,
    lead_time_setting_id?: number,
    lead_time_title?: string,
    disabled?: boolean
  }, title: string): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importPartnerSettingModalComp(),
        compData: data,
        dialogTitle: title,
      },
      width: '700px',
      panelClass: 'calenso-generic-dialog-box',
    });
    return dialogRef;
  }

  openJsonDiffViewerDialog(
    data: {
      json: {
        [key: string]: string
      },
      showJsonParser: boolean,
      showLeftOnly?: boolean,
      showMeta?: boolean,
    },
    title: string
  ) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importJsonDiffViewerComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '700px',
    });
    return dialogRef;
  }

  openJsonViewerDialog(data: {
    json: string,
    showJsonParser: boolean
  }, title: string) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importJsonViewerComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '700px',
    });
    return dialogRef;
  }

  openWebhookReplayHistoryDialog(uuid: string) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importWebhookReplayHistoryComp(),
        compData: { uuid },
        dialogTitle: 'webhook_stat_component.replay_history_title',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '700px',
      autoFocus: false,
    });
    return dialogRef;
  }

  openGraphApiLogSettingsDialog(): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importGraphApiLogSettingsDialogComp(),
        compData: {},
        dialogTitle: 'graph_api_logs.settings_dialog_title',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '560px',
      autoFocus: false,
    });
  }

  showAuditLogsData(
    data: AuditLogsDbModel,
    title: string
  ) {
    const dialogRef: MatDialogRef<CalioDialogComponent, any> = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.showAuditLogData(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '700px',
    });
    return dialogRef;
  }

  openTaxesFormDialog(data: {
    formType: string,
    taxDbModel: TaxDbModel
  }, title: string) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importTaxesFormComp(),
        compData: data,
        dialogTitle: title,
      },
      width: '500px',
      panelClass: 'calenso-generic-dialog-box',
    });
    return dialogRef;
  }

  openConnectStripeStepperDialog(data: {
    isStripeEnabled: boolean,
    isDialogLayout: boolean
  }, title: string) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importConnectStripeStepperComp(),
        compData: data,
        dialogTitle: title,
      },
      width: '610px',
      disableClose: true,
      panelClass: 'calenso-generic-dialog-box',
    });
    return dialogRef;
  }

  openCalensoWidgetDialog(data: {
    partnerBookingName: string,
    secret: string,
    internal: boolean,
    token: string,
    loggedInWorker?: WorkerDbModel,
    customer?: CustomerDbModel,
  }, title: string, customHeaderClass: string) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importCalensoWidgetModalComp(),
        compData: data,
        dialogTitle: title,
        customHeaderClass,
      },
      panelClass: 'calenso-widget-dialog-box',
      width: '1000px',
    });
    return dialogRef;
  }

  openRebookAppointmentDialog(data: {
    uuid: string,
    appointment: CalendarAppointmentModel,
    partner: PartnerDbModel,
    userMode: string,
    hash?: string,
  }) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importRebookAppointmentComp(),
        compData: data,
        dialogTitle: 'appointment_detail_component.rebook_appointment',
      },
      width: '1000px',
      panelClass: 'calenso-generic-dialog-box'
    });
    return dialogRef;
  }

  openDeleteEventConfirmationDialog(data: {
    confirmationMsg?: string,
    confirmBtn?: string,
    cancelBtn?: string,
    showToggleButton?: boolean,
    toggleButtonTitle?: string,
    showCancelReasonTextArea?: boolean,
    cancelReason?: string,
    isCancelReasonRequired?: boolean,
    cancelReasonHelperText?: string,
    originalEventName?: string,
  }, title: string) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importDeleteEventConfirmationModalComp(),
        compData: data,
        dialogTitle: title,
      },
      maxWidth: '500px',
    });
    return dialogRef;
  }

  openConfirmationDialog(data: ConfirmationModal, title?: string, width?: string) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importConfirmationModalComp(),
        compData: data,
        dialogTitle: title,
      },
      width
    });
    return dialogRef;
  }


  openAppointmentFormDialog(data: {
    appointment: CalendarAppointmentModel,
    workers: WorkerDbModel[]
  }, title: string) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        compData: data,
        dialogTitle: title,
      },
      width: '500px',
      panelClass: 'calenso-generic-dialog-box',
    });
    return dialogRef;
  }

  openEventSlotParticipantDialog(data: {
    participants: {
      regular: EventParticipantDbModel[],
      waitingList: EventParticipantDbModel[]
    },
    slot: EventSlotDbModel,
    event: EventDbModel
  }, title: string) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importEventSlotParticipantComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '800px',
    });
    return dialogRef;
  }

  openEventSlotEditDialog(data: {
    slot: EventSlotDbModel,
    event: EventDbModel
  }, title: string) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importEventSlotEditComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '600px'
    });
    return dialogRef;
  }

  openSmartActionTriggerDialog(data: {
    smartActionTrigger: PartnerSettingModel,
    disabled?: boolean
  }, title: string) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importSmartActionTriggerComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '500px'
    });
    return dialogRef;
  }

  openImageUploadDialog(
    data: {
      imageUploadType?: string,
      workerDbModel?: WorkerDbModel
    },
    title: string,
    modelWidth = '500px'
  ) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importImageUploadDialogComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: modelWidth
    });
    return dialogRef;
  }

  openDeleteAccountConfirmationDialogComp(data: Record<string, unknown>, title: string) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importDeleteAccountConfirmationDialogComp(),
        compData: data,
        dialogTitle: title,
      },
    });
    return dialogRef;
  }

  openCustomerBookingDeleteDialog(data: {
    bookings: CustomBookingDbModel[],
    landingpageDeactivateCancellationReasonFeature: PartnerSettingModel,
    landingpageCancellationReasonIsOptionalFeature: PartnerSettingModel
  }, title: string) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importCustomerBookingDeleteComp(),
        compData: data,
        dialogTitle: title,
      },
    });
    return dialogRef;
  }

  openEventCopyCodeDialog(data: {
    config: {
      imagePath: string,
      imageWidth: number,
      localeTitleKey: string,
      localeDescKey: string,
      links: [{
        displayScriptCode: string,
        displayLinkCode: string,
        codeToCopy: string,
      }]
    }
  }, title: string) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importEventCopyCodeComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '500px'
    });
    return dialogRef;
  }

  openEventAddSlotDialog(data: Record<string, unknown>, title: string) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importEventAddSlotBottomSheetComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '560px',
    });
    return dialogRef;
  }

  openBryntumCalendarEventEditorDialog(data: {
    eventRecord: BryntumCalendarEventModel,
    resourceRecord: BryntumCalendarResourceModel,
    partnerSettings: PartnerSettingModel[]
    partner: PartnerDbModel
  }, title: string) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importBryntumEventEditorComp(),
        compData: data,
        dialogTitle: title,
      },
      autoFocus: true,
      panelClass: 'calenso-generic-dialog-box',
      width: '1400px'
    });
    return dialogRef;
  }

  openCalioPermissionModal() {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importCalioPermissionModalComp(),
        compData: {},
        dialogTitle: 'Permission Management',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '500px'
    });
    return dialogRef;
  }

  createApiModal(
    data: {
      is_super_admin: boolean,
      partner_name: string,
      partner_uuid: string
    },
    title: string
  ) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.createApiComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '600px'
    });
    return dialogRef;
  }

  openMeetingSetupDialog(
    data: {
      code?: string,
      meetingType?: string,
      workerId?: number,
      eventId?: number,
      showWorkerOptionsInForm?: boolean,
      showEventOptionsInForm?: boolean,
      redirectTo?: number,
      source?: string
    },
    title: string
  ): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importMeetingSetupDialogComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '650px',
    });
    return dialogRef;
  }

  openReleaseNotesDialog(
    data: {
      releaseNotes: ReleaseNotesModel,
    },
    title: string): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importReleaseNotesDialogComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '520px',
    });
    return dialogRef;
  }

  openPartnerReleaseNotesDialog(
    data: {
      releaseNotes: PartnerReleaseNotesModel[]
    },
    title: string, subtitle: string): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importPartnerReleaseNotesDialogComp(),
        compData: data,
        dialogTitle: title,
        dialogSubTitle: subtitle,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '520px',
    });
    return dialogRef;
  }

  openSmartWidgetEmbedCodeDialog(
    data: {
      widgetUUID: string,
      partnerUUID: string,
      showSurveyUrl: boolean
    },
    title: string): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importSmartWidgetEmbedCodeDialogComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '520px',
    });
    return dialogRef;
  }

  openSmartWidgetCreateDialog(): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importSmartWidgetCreateDialogComp(),
        compData: {},
        dialogTitle: 'smart_widget_comp.create_smart_widget_title',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '600px',
      autoFocus: false,
      // ESC / backdrop click would silently discard a typed title; require an
      // explicit Cancel-button or close-icon gesture instead.
      disableClose: true,
    });
    return dialogRef;
  }

  openSwWidgetStepDialog(data: {
    step?: SmartWidgetStepModel;
    bookingQuestions: CustomFieldModel[];
    existingSteps?: SmartWidgetStepModel[];
    partner?: PartnerDbModel;
  }): MatDialogRef<CalioDialogComponent, SmartWidgetStepModel | null> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importSwWidgetStepDialogComp(),
        compData: data,
        dialogTitle: data?.step?.id
          ? 'smart_widget_comp.edit_widget_step'
          : 'smart_widget_comp.add_widget_step',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '520px',
      autoFocus: false,
    });
    return dialogRef;
  }

  openTemplateRestrictionDialog(data: {
    disabled?: boolean
  }, title: string): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importTemplateRestrictionDialogComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '520px',
    });
    return dialogRef;
  }

  openBookingQuestionOrderingDialog(
    data: {
      rawTitle: string,
      selectedTitle: string,
      pageType: string,
      selectedFormFields: CustomFieldModel[],
      deSelectedFormFields: CustomFieldModel[],
      stepDbModel?: SmartWidgetStepModel,
    },
    title: string,
  ): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importBookingQuestionOrderingComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      // Two-column Available/Configured drag-drop. 1000px gives each column
      // ~470px of breathing room; maxWidth caps it on smaller laptops, and the
      // BookingQuestionOrderingComponent's @media query collapses the grid to
      // a single column below 768px so the dialog stays usable on mobile.
      width: '1000px',
      maxWidth: '95vw',
    });
    return dialogRef;
  }

  openHistoryDialog(
    data: {
      history: CustomerCustomFieldDbModel[]
      customFields: CustomFieldOptionModel[]
      customFieldType: string
    },
    title: string): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.formFieldHistory(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '750px',
    });
    return dialogRef;
  }

  openAuditLogsExportDialog(
    title: string
  ): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(
      CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.auditLogsExport(),
        compData: {},
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '520px',
    });
  }

  openWebhookDialog(
    title: string,
    data?: WebhookEndpoints[]
  ): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(
      CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.webhook(),
        compData: { data },
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '520px',
    });
  }

  openAppointmentStatusDialog(
    data: {
      formType: string,
      appointmentStatusModel: AppointmentStatusModel,
    },
    title: string,
  ): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(
      CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.appointmentStatus(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '520px',
    });
  }

  openPostcodeMappingDialog(
    data: {
      formType: 'create' | 'edit',
      mapping: PostCodeDataModel
      countries: CountryDbModel[]
    },
    title: string
  ): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.postcodeMappingForm(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '700px',
      disableClose: true
    });
  }

  openUpdateAppointmentStatusDialog(
    data: {
      appointment: CalendarAppointmentModel,
      partner: PartnerDbModel
    },
    title: string,
  ): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(
      CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.updateAppointmentStatus(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '700px',
    });
  }

  openAppointmentStatusHistoryDialog(
    data: {
      statuses: AppointmentStatusDbModel[],
      statusList: AppointmentStatusModel[]
    },
    title: string,
  ): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(
      CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.showAppointmentStatusHistory(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '80vw',
    });
  }

  openYoutubeVideoPreviewDialog(
    option: CustomFieldOptionModel
  ): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(
      CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.youtubeVideoPreview(),
        compData: {
          youtubeUrl: option.youtube_url
        },
        dialogTitle: null,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '520px',
    });
  }

  openSortMeetingProvidersDialog(
    data: {
      meetingProvider: MeetingsDbModel[],
      templates: TemplateDbModel[]
    },
    title: string,
    subtitle: string
  ): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.sortMeetingProvider(),
        compData: data,
        dialogTitle: title,
        dialogSubTitle: subtitle
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '520px',
    });
    return dialogRef;
  }

  openCancelSubscriptionDialog(
    data: {
      cancellationDate: string
    },
    title: string,
    subtitle?: string
  ): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.cancelSubscription(),
        compData: data,
        dialogTitle: title,
        dialogSubTitle: subtitle
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '520px',
    });
    return dialogRef;
  }

  openManageSubscriptionDialog(
    data: {
      customer_uuid: string,
      active_subscription: ActiveSubscriptionDbModel,
      partner_id: number,
    },
    title: string
  ): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(
      CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.manageSubscription(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '520px',
    });
  }

  openEmailSmsPreviewDialog(
    data: {
      uuid: string,
    },
    title: string,
  ) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.emailSmsPreview(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '700px',
    });
    return dialogRef;
  }

  openHtmlPreviewDialog(
    data: {
      htmlContent: SafeHtml
    },
    title: string,
  ) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.openHtmlPreview(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '700px',
    });
    return dialogRef;
  }

  openEmailHistoryDialog(data: { uuid: string }) {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.emailHistory(),
        compData: data,
        dialogTitle: 'email_sms_view_settings.history',
        dialogSubTitle: 'email_sms_view_settings.history_subtitle',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '700px',
    });
  }

  openBookingNotificationHistoryDialog(data: { customer: CustomerDbModel; appointmentId: number }, title: string): MatDialogRef<any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.customerNotificationHistory(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '900px',
      autoFocus: false,
      scrollStrategy: this.overlay.scrollStrategies.block(),
    });
  }

  openResetWorkerMfaDialog(
    data: {
      resetMfa: boolean,
      verifyMfa: boolean,
      worker: WorkerDbModel
    },
    title: string,
    subtitle?: string
  ): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.resetWorkerMfa(),
        compData: data,
        dialogTitle: title,
        dialogSubTitle: subtitle
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '520px',
    });
    return dialogRef;
  }

  openActivateMfaDialog(
    data: {
      mfaData: MfaDbModel
    },
    title: string,
    subtitle?: string
  ): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.activateMfa(),
        compData: data,
        dialogTitle: title,
        dialogSubTitle: subtitle
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '620px',
    });
    return dialogRef;
  }

  openMfaBackupCodesDialog(
    data: {
      mfaData: { backup_codes: number[] }
    },
    title: string,
    subtitle?: string
  ): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.showMfaBackupCodes(),
        compData: data,
        dialogTitle: title,
        dialogSubTitle: subtitle
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '620px',
      disableClose: true
    });
    return dialogRef;
  }

  openAvailableWorkerDialog(
    data: {
      workers: WorkerDbModel[]
    },
    title: string,
    subtitle?: string
  ): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.showPossibleWorkers(),
        compData: data,
        dialogTitle: title,
        dialogSubTitle: subtitle
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '520px',
    });
    return dialogRef;
  }

  openGlobalNotificationDialog(
    data: {
      partner: PartnerDbModel;
      notification?: NotificationDbModel,
    },
    title: string,
    subtitle?: string
  ): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.showGlobalNotification(),
        compData: data,
        dialogTitle: title,
        dialogSubTitle: subtitle
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '620px',
      disableClose: true
    });
    return dialogRef;
  }

  openCustomerModalForm(
    data: {
      requiredCustomerFormFieldsPartnerSetting: PartnerSettingModel
    },
    title: string,
  ): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.openCustomerModalForm(),
        compData: data,
        dialogTitle: title
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '700px',
      disableClose: true
    });
    return dialogRef;
  }

  openImagePreviewDialog(
    data: {
      imageUrl: string
    },
  ): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.ImageModalComponent(),
        compData: data
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '700px'
    });
    return dialogRef;
  }

  openShowMappedServicesDialog(
    data: {
      customFieldDbModel?: CustomFieldModel,
      meeting?: MeetingsDbModel,
      selectedWorker?: WorkerDbModel,
      workerUuid?: string,
      isMeetingModal: boolean,
      isReadOnly?: boolean
    },
    title: string,
    subTitle: string
  ): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.mappedServicesDialog(),
        compData: data,
        dialogTitle: title,
        dialogSubTitle: subTitle
      },
      width: '450px',
      panelClass: 'calenso-generic-dialog-box',
    });
  }

  openRebookGroupAppointmentDialog(data: {
    uuid: string,
    event: BookingEventDao,
    partner: PartnerDbModel,
    userMode: string,
    hash?: string,
  }) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.rebookGroupAppointmentDialog(),
        compData: data,
        dialogTitle: 'appointment_detail_component.rebook_appointment',
      },
      width: '1000px',
      panelClass: 'calenso-generic-dialog-box',
      disableClose: true
    });
    return dialogRef;
  }

  generateQrCodeDialog(
    data: {
      type: string,
      bookingLinksParams?: BookingLinkGenerator
      base64StringDownloadString?: string,
      base64StringPreviewString?: string,
      linkToCopy?: string
    },
    title: string,
  ): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.generateQrCodeComponent(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '600px',
      disableClose: true
    });
    return dialogRef;
  }

  deleteStoreConfirmationDialog(data: {
    stores: StoreDbModel[],
    /**
     * The stores the caller may re-parent INTO, pre-scoped to the backend's own target gate.
     * Optional: omitted, the dialog falls back to `stores` and behaves as it did before.
     */
    reparentCandidates?: StoreDbModel[],
    currentStoreId: number,
    workersCount: number,
    childrenCount?: number
  }) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.deleteStoreConfirmationDialog(),
        compData: data,
        dialogTitle: 'confirmation_modal_component.confirm_deletion',
      },
      width: '450px',
      panelClass: 'calenso-generic-dialog-box',
    });
    return dialogRef;
  }

  thirdPartyIntegratinoApiErrorDialog(data: ApiErrorDialog) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.thirdPartyIntegratinoApiErrorDialog(),
        compData: data,
        dialogTitle: 'external_calendar_settings_component.calendar_connection_error_dialog',
      },
      width: '600px',
      panelClass: 'calenso-generic-dialog-box',
    });
    return dialogRef;
  }

  conferenceWorkersList(data: {
    workers: ConferenceWorkerModel[]
  }) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.conferenceWorkersListDialog(),
        compData: data,
        dialogTitle: 'conference_dashboard_component.attendees',
      },
      width: '450px',
      panelClass: 'calenso-generic-dialog-box',
    });
    return dialogRef;
  }

  supportUserLoginDialog(data: {
    supportPartnerUuid: string;
  }) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.supportUserLoginDialog(),
        compData: data,
        dialogTitle: 'Login using the Support user',
      },
      width: '450px',
      panelClass: 'calenso-generic-dialog-box',
    });
    return dialogRef;
  }

  openExtendTestModeDialog(data: {
    supportPartner: PartnerDbModel;
    currentTrialMax: number;
    trialMode: string;
  }) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.extendTestModeDialog(),
        compData: data,
        dialogTitle: 'customer_admin_section.extend_test_mode',
      },
      width: '600px',
      panelClass: 'calenso-generic-dialog-box',
    });
    return dialogRef;
  }

  createIntegrationConfigDialog(data: {
    configuration: WidgetConfiguration
  }, title: string) {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.createIntegrationConfigDialog(),
        compData: data,
        dialogTitle: title,
      },
      width: '450px',
      panelClass: 'calenso-generic-dialog-box',
    });
    return dialogRef;
  }

  openCustomerLinksTableDialog(
    data: {
      appointments: CalendarAppointmentModel[],
      partnerHash: string
    }
  ): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.customerLinksTableDialog(),
        compData: data,
        dialogTitle: 'conference_dashboard_component.bookings',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '600px',
    });
    return dialogRef;
  }

  openMergeCustomerDialog(
    data: {
      customers: CustomerDbModel[]
    }
  ): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.mergeCustomerDialog(),
        compData: data,
        dialogTitle: 'crm_component.merge_customer',
        dialogSubTitle: 'crm_component.merge_customers_hint'
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '650px',
    });
    return dialogRef;
  }

  openPredefineWorkflowListDialog(data: { partnerSetting: PartnerSettingModel, isWorkflowEnabledFirstTime: boolean }): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.predefineWorkflowListDialog(),
        compData: data,
      },
      disableClose: true,
      panelClass: 'calenso-generic-dialog-box',
      width: '550px',
    });
    return dialogRef;
  }

  openAnalyticsFeatureNoteDialog(): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.analyticsFeatureNoteDialog(),
        compData: {}
      },
      disableClose: true,
      panelClass: 'calenso-generic-dialog-box',
      width: '550px',
    });
    return dialogRef;
  }

  openAnalyticsUpgradeDialog(data?: {
    featureType?: 'charts' | 'metrics' | 'trends' | 'general' | 'revenue' | 'dateRange' | 'historicalData' | 'eventAnalytics';
    requiredTier?: string;
    currentLimit?: number;
    requestedLimit?: number;
  }): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.analyticsUpgradeDialog(),
        compData: data || {},
        dialogTitle: 'analytics_component.upgrade_title'
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '500px',
      scrollStrategy: this.overlay.scrollStrategies.block(),
    });
    return dialogRef;
  }

  openAnalyticsPresetSaveDialog(data: {
    filterConfig: AnalyticsFilterConfig;
    /** Set only for act-on-behalf (super-admin/reseller viewing another partner). */
    partnerUuid?: string;
  }): MatDialogRef<CalioDialogComponent, AnalyticsFilterPresetModel | null> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.analyticsPresetSaveDialog(),
        compData: data,
        dialogTitle: 'analytics_component.save_filter',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '450px',
      autoFocus: false,
    });
  }

  openBookingTypesFormDialog(data: {
    formType: string,
    bookingType: BookingTypesDbModel,
  }, title: string): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.openBookingTypesFormDialog(),
        compData: data,
        dialogTitle: title,
      },
      width: '500px',
      panelClass: 'calenso-generic-dialog-box',
    });
    return dialogRef;
  }

  openWorkflowListDetailsDialog(data: { workflow: WorkflowsModel, partner: PartnerDbModel }): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.workflowListDetailsDialog(),
        compData: data,
        dialogTitle: 'workflow.title',
        showDialogCloseButton: false,
      },
      disableClose: true,
      panelClass: 'calenso-generic-dialog-box',
      width: '600px',
    });
    return dialogRef;
  }

  createCustomEmailTemplate(data: { mode: string }): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.createCustomEmailTemplateDialog(),
        compData: data,
        dialogTitle: 'custom_email_templates.custom_email_templates_title',
      },
      disableClose: true,
      panelClass: 'calenso-generic-dialog-box',
      width: '600px',
    });
    return dialogRef;
  }

  openBookingTypeAttributeFormDialog(data: {
    formType: string,
    bookingTypeAttribute: BookingTypeAttributeDbModel,
  }, title: string): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.openBookingTypeAttributeFormDialog(),
        compData: data,
        dialogTitle: title,
      },
      width: '500px',
      panelClass: 'calenso-generic-dialog-box',
      disableClose: true,
    });
    return dialogRef;
  }

  customEmailTemplatePreview(data: { previewEmailFilename: string, authorizationToken: string, templatePreviewData?: Record<string, any> }): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.customEmailTemplatePreviewDialog(),
        compData: data,
        dialogTitle: 'custom_css.preview',
        headerAction: {
          labelKey: 'custom_email_templates.configure_placeholders',
          callback: () => this.openConfigurePlaceholdersDialog()
        }
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '1000px',
    });
    return dialogRef;
  }

  createHighlightedDaysWeeks(data: { highlightedData?: any }, title: string): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.createHighlightedDaysWeeksDialog(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '700px',
    });
    return dialogRef;
  }

  openAddOnsDialog(data: { addOn?: AddOnModel }, title: string): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef: MatDialogRef<CalioDialogComponent, any> = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.openAddOnsDialog(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '700px',
    });
    return dialogRef;
  }

  openPartnerAddOnListDialog(data: { partnerAddOns: AddOnPartnerModel[] }, title: string): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef: MatDialogRef<CalioDialogComponent, any> = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.openPartnerAddOnListDialog(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '700px',
    });
    return dialogRef;
  }

  managePartnerAddOns(data: { addOns?: AddOnModel[], partnerUuid: string }): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef: MatDialogRef<CalioDialogComponent, any> = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.managePartnerAddOnsDialog(),
        compData: data,
        dialogTitle: 'add_ons_settings.manage_partner_addons',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '700px',
    });
    return dialogRef;
  }

  openCloneWorkerDialog(data: { worker: WorkerDbModel }, title: string, subtitle: string): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.cloneWorkerDialog(),
        compData: data,
        dialogTitle: title,
        dialogSubTitle: subtitle
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '500px',
    });
  }


  informationDialog(data: { message: string }, title: string): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.informationDialog(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '500px',
    });
  }

  openClonePartnerDialog(data: { partner: PartnerDbModel; type?: CloneType }, title: string): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.clonePartnerDialog(),
        compData: data,
        dialogTitle: title
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '500px',
    });
  }

  openImportVoucherDialog(): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importVoucherDialog(),
        dialogTitle: 'coupons_dashboard_component.import_vouchers',
        compData: {}
      },
      disableClose: true,
      panelClass: 'calenso-generic-dialog-box',
      width: '500px',
    });
  }

  openImportAppointmentsDialog(): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importAppointmentsDialog(),
        dialogTitle: 'appointment_dashboard_component.import_appointments',
        compData: {},
      },
      disableClose: true,
      panelClass: 'calenso-generic-dialog-box',
      width: '550px',
    });
  }

  openBulkAssignDialog(compData: Record<string, unknown>): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.bulkAssignDialog(),
        compData,
        dialogTitle: 'resource_planning.bulk_assign_title',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '600px',
      autoFocus: false,
    });
  }

  openConflictConfirmDialog(compData: Record<string, unknown>): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.conflictConfirmDialog(),
        compData,
        dialogTitle: 'resource_planning.conflict_dialog_title',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '500px',
      autoFocus: false,
    });
  }

  openAzureWorkerStoreMappingDialog(data: { mapping?: AzureStoreWorkerModel }, title: string): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importAzureWorkerStoreMappingFormDialog(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '550px',
    });
  }

  openAzureRoleMappingDialog(
    data: { mapping?: AzureRoleMappingModel },
    title: string
  ): MatDialogRef<CalioDialogComponent, { id: number } | undefined> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importAzureRoleMappingFormDialog(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '640px',
    });
  }

  openAzureRoleSyncDialog(
    data: { mapping: AzureRoleMappingModel }
  ): MatDialogRef<CalioDialogComponent, AzureRoleSyncStartResponse | undefined> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importAzureRoleSyncDialog(),
        compData: data,
        dialogTitle: 'azure_role_mapping.sync_existing',
        dialogSubTitle: 'azure_role_mapping.sync_dialog_subtitle',
        dialogSubtitleTranslationJson: { role: data.mapping?.role?.name },
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '640px',
      autoFocus: false,
    });
  }

  openBookingLimitOverrideDialog(
    data: {
      isEditMode?: boolean;
      override?: BookingLimitRuleDbModel;
    },
    title: string
  ): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importBookingLimitOverrideModalDialog(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '600px',
    });
  }

  openAddPriorityGroupDialog(): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importAddPriorityGroupDialog(),
        compData: {},
        dialogTitle: 'priority_groups.create_dialog.title',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '480px',
      autoFocus: false,
    });
  }

  openServiceRoundRobinOverrideDialog(
    data: {
      serviceUuid: string;
    }
  ): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importPriorityGroupServiceOverrideDialog(),
        compData: data,
        dialogTitle: 'service_round_robin_override.dialog_title',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '720px',
      autoFocus: false,
    });
  }

  openCustomerImportSettingsDialog(data: { partner: PartnerDbModel }): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.customerImportSettingsDialog(),
        compData: data,
        dialogTitle: 'breadcrum_component.customer_import_settings',
        dialogSubTitle: 'section.customer_import_subtitle',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '700px',
    });
  }

  openTriggerManualWorkflowDialog(
    data: {
      customers?: CustomerDbModel[],
      workflow?: WorkflowsModel
    }
  ): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.triggerManualWorkflowDialog(),
        compData: data,
        dialogTitle: 'crm_component.manually_trigger_workflow',
        dialogSubTitle: 'crm_component.manually_trigger_workflow_hint'
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '650px',
    });
  }

  upcomingCustomerAppointmentsDialog(data: { customerUuids: string[], upComingAppointments: CustomerUpcomingAppointmentModel[] }) {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.upcomingCustomerAppointmentsDialog(),
        compData: data,
        dialogTitle: 'crm_component.customer_has_open_bookings_heading',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '500px',
    });
  }

  openEmployeeServicesDialog(data: { worker: WorkerDbModel }, title: string): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.employeeServicesDialog(),
        dialogTitle: title,
        compData: data
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '500px',
    });
  }

  openRebookAppointmentHistoryDialog(data: { appointment: CalendarAppointmentModel }): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.rebookAppointmentHistoryDialog(),
        compData: data,
        dialogTitle: 'crm_component.rebooking_history'
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '700px',
    });
  }

  openCustomerListDialog(data: {
    partner: PartnerDbModel,
    isQuickFilterMode?: boolean,
    isMultiSelectMode?: boolean,
    preSelectedCustomers?: CustomerDbModel[]
  }): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.customerListDialog(),
        compData: data,
        dialogTitle: 'event_view_component.search_customers'
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '1200px',
    });
  }

  // afterClosed() emits `true` when the user saved; caller refreshes its list on truthy.
  openEditCouponDialog(data: { couponUuid: string }): MatDialogRef<CalioDialogComponent, boolean> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.createCouponDialog(),
        compData: data,
        dialogTitle: 'app_create_coupon.title_edit_voucher'
      },
      panelClass: ['calenso-generic-dialog-box', 'coupon-edit-dialog-box'],
      width: '1100px',
      autoFocus: false,
    });
  }

  createOrEditSpamPrevention(
    data: {
      spamDetails: SpamPreventionDBModel,
      partner: PartnerDbModel,
      searchPartnerUuid: string
    },
    title: string,
    subtitle?: string
  ) {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.createOrEditSpamPrevention(),
        compData: data,
        dialogTitle: title,
        dialogSubTitle: subtitle
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '600px'
    });
  }

  openPasswordProtectionDialog(
    data: {
      appointment?: CalendarAppointmentModel,
      partner: PartnerDbModel,
      userMode: string,
      lang: string,
      type: 'appointment' | 'event',
      event?: BookingEventDao,
    },
    dialogTitle: string,
    showDialogCloseButton = true
  ): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.passwordProtectionDialog(),
        compData: data,
        dialogTitle,
        showDialogCloseButton
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '800px',
      disableClose: true,
    });
  }

  openServicePackageDialog(data: {
    servicePackage: AppointmentServiceDbModel;
  }, title: string): MatDialogRef<CalioDialogComponent, any> {
    const dialogRef = this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importServicePackageComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '800px',
    });
    return dialogRef;
  }

  openImportPostcodesDialog(): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importPostcodeImportDialogComp(),
        compData: {},
        dialogTitle: 'settings_component.postcode_import',
        // Cancel is the single exit: it is disabled while an upload is in flight, and a header
        // close would abandon that same upload with no message.
        showDialogCloseButton: false,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '700px',
      disableClose: true
    });
  }

  openConfigurePlaceholdersDialog(): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importConfigurePlaceholdersComp(),
        compData: {},
        dialogTitle: 'custom_email_templates.configure_placeholders',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '700px',
    });
  }

  openTestSendRecipientDialog(data: { channel: TestSendChannel }): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importTestSendRecipientDialogComp(),
        compData: data,
        dialogTitle: 'workflow.test_send_recipient_title',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '440px',
      autoFocus: false,
    });
  }

  openCompleteProfileDialog(data: { partner: PartnerDbModel; autoDetectCountry?: boolean }): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importCompleteProfileModalComp(),
        compData: data,
        dialogTitle: 'almost_there.complete_profile_modal.title',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '750px',
      disableClose: true
    });
  }

  openBookingWidgetDialog(data: { partner: PartnerDbModel }): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importBookingWidgetModalComp(),
        compData: data,
        dialogTitle: 'almost_there.booking_widget_title',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '900px',
      disableClose: false
    });
  }

  openOnlineCalendarSettingsDialog(): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importOnlineCalendarSettingsDialogComp(),
        dialogTitle: 'settings_component.online_calendar',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '950px'
    });
  }

  openCalioColumnConfigDialog(
    data: CalioColumnConfigData,
    title: string,
  ): MatDialogRef<CalioDialogComponent, string[] | null> {
    return this.matDialog.open<CalioDialogComponent, unknown, string[] | null>(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importCalioColumnConfigComp(),
        compData: data,
        dialogTitle: title,
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '520px',
    });
  }

  openResourcePlanningDialog() {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.resourcePlanningDialog(),
        compData: {},
        dialogTitle: 'resource_planning.page_title',
        disableDrag: true,
      },
      panelClass: ['calenso-generic-dialog-box', 'resource-planning-dialog-panel'],
      width: '98vw',
      maxWidth: '98vw',
      height: '95vh',
      disableClose: true,
      autoFocus: false,
      scrollStrategy: this.overlay.scrollStrategies.block(),
    });
  }

  openForecastDayDetailDialog(data: ForecastDayDetailData): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.forecastDayDetailDialog(),
        compData: data,
        dialogTitle: 'forecast_component.day_detail_title',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '560px',
      autoFocus: false,
    });
  }

  openForecastKeywordSettingsDialog(): MatDialogRef<CalioDialogComponent, boolean> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.forecastKeywordSettingsDialog(),
        compData: {},
        dialogTitle: 'forecast_component.keyword_settings_title',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '620px',
      autoFocus: false,
    });
  }

  openBlockedIpsDialog(): MatDialogRef<CalioDialogComponent, any> {
    return this.matDialog.open(CalioDialogComponent, {
      data: {
        loadComponent: this.importDynamicComponentService.importBlockedIpsDialogComp(),
        compData: {},
        dialogTitle: 'signup_rules_settings.blocked_ips.dialog_title',
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '900px',
    });
  }
}
